import { Kitchen } from '../kitchen.class';
import { KitchenEstimateLineItem } from '../types/kitchen-estimate-line-item.type';

type PromoChild = Kitchen['promotionalSkus']['children'][number];

const toPromoLineItem = (promotional: PromoChild): KitchenEstimateLineItem => {
	const { sku, description, adjustmentType, priceAdjustment, totalAdjustment, hasDiscountRules, quantity, isDiscount } = promotional;
	const isFreeShippingPromo = sku === 'FREE-SHIPPING';
	const label = (() => {
		if (isFreeShippingPromo) return 'Free Shipping';
		if (hasDiscountRules || adjustmentType === 'FIXED') return description;
		return `${description} (${priceAdjustment}%)`;
	})();
	// A FIXED promo subtracts priceAdjustment x matched items x quantity (totalAdjustmentCfn), so
	// showing the per-unit rate here understated the discount the subtotal already took.
	const price = totalAdjustment;
	const priceNote = (() => {
		if (isFreeShippingPromo || hasDiscountRules) return undefined;
		if (priceAdjustment >= 0) return undefined;
		if (adjustmentType === 'FIXED') return `$${priceAdjustment}`;
		return `(${priceAdjustment})%`;
	})();
	return {
		sku,
		label,
		description,
		category: isDiscount ? 'Discount' : 'Promotion',
		group: isDiscount ? 'discount' : 'promotion',
		qty: quantity ?? 1,
		each: totalAdjustment,
		total: totalAdjustment,
		price,
		kind: 'promo',
		...(priceNote ? { priceNote } : {})
	};
};

/**
 * Builds the canonical estimate rows consumed by EVERY estimate surface (in-app panel,
 * estimate PDF, design-package payload). Row selection, ordering, and the price-column
 * value are decided here once so the surfaces can never disagree. A product row's price is
 * the LINE total (qty x each): every surface shows one price column beside a quantity, so a
 * per-unit value there reads as a line that failed to multiply.
 * @param kitchen Kitchen to build the estimate rows for
 * @returns ordered rows: islands, countertops, backsplashes, appliances, accessories, discounts, promotions
 */
export const getEstimateLineItemsCfn = (kitchen: Kitchen): Array<KitchenEstimateLineItem> => {
	const { itemizedMsrp, promotionalSkus, material } = kitchen;
	const surroundName = material.surround.displayName;
	const rows: Array<KitchenEstimateLineItem> = [];

	// Islands
	itemizedMsrp.islands.forEach((island) => {
		const [islandName] = island.description.split(' | ');
		rows.push({
			sku: island.sku,
			label: `${islandName} - ${surroundName}`,
			description: island.description,
			category: 'island',
			group: 'island',
			qty: island.qty,
			each: island.each,
			total: island.total,
			price: island.total,
			kind: 'product',
			...(island.discontinued ? { discontinued: true } : {})
		});
	});

	// Countertops -- exclude RTA-Chiseled (included in island pricing)
	itemizedMsrp.countertops
		.filter((item) => !item.sku.startsWith('RTA-Chiseled'))
		.forEach((item) => {
			rows.push({
				sku: item.sku,
				label: item.description,
				description: item.description,
				category: item.category,
				group: 'countertop',
				qty: item.qty,
				each: item.each,
				total: item.total,
				price: item.total,
				kind: 'product',
				...(item.discontinued ? { discontinued: true } : {})
			});
		});

	// Backsplashes -- single aggregated square-footage row
	itemizedMsrp.backsplashes.forEach((backsplash) => {
		rows.push({
			sku: backsplash.sku,
			label: backsplash.description,
			description: backsplash.description,
			category: backsplash.category,
			group: 'backsplash',
			qty: backsplash.qty,
			each: backsplash.each,
			total: backsplash.total,
			price: backsplash.total,
			kind: 'product',
			...(backsplash.warning ? { warning: backsplash.warning } : {}),
			...(backsplash.discontinued ? { discontinued: true } : {})
		});
	});

	// Appliances/items -- product label is `label`
	itemizedMsrp.items.forEach((item) => {
		rows.push({
			sku: item.sku,
			label: item.label ?? '',
			description: item.description,
			category: item.category,
			group: 'appliance',
			qty: item.qty,
			each: item.each,
			total: item.total,
			price: item.total,
			kind: 'product',
			...(item.discontinued ? { discontinued: true } : {}),
			...(item.isCustomOverride ? { isCustomOverride: true } : {})
		});
	});

	// Accessories
	itemizedMsrp.accessories.forEach((accessory) => {
		rows.push({
			sku: accessory.sku,
			label: accessory.description,
			description: accessory.description,
			category: 'accessory',
			group: 'accessory',
			qty: accessory.qty,
			each: accessory.each,
			total: accessory.total,
			price: accessory.total,
			kind: 'product',
			...(accessory.discontinued ? { discontinued: true } : {})
		});
	});

	// Discounts first, then promotions
	promotionalSkus.children.filter((p) => p.isDiscount).forEach((p) => rows.push(toPromoLineItem(p)));
	promotionalSkus.children.filter((p) => !p.isDiscount).forEach((p) => rows.push(toPromoLineItem(p)));

	return rows;
};
